import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRightIcon } from '@utils/icons';
import type { AiBriefRef } from '@apis/aiAgent';
import { BriefInsights } from '@components/Pages/AiAgentPage/MorningBrief/BriefInsights';
import { formatBriefDate } from '@components/Pages/AiAgentPage/MorningBrief/briefDay';
import { useBriefInsights } from '@components/Pages/AiAgentPage/MorningBrief/useBriefInsights';
import { useNamedGreeting } from '@components/Pages/AiAgentPage/useGreeting';
import { DashboardCard } from './_shared/DashboardCard';
import styles from './morningBriefCard.module.css';

/**
 * THE MORNING BRIEF WHERE THE MORNING STARTS.
 *
 * The dashboard is the first screen of the day, so the brief's greeting, the
 * state of the site and what deserves attention belong on it — not one click
 * away behind the assistant, where a reader has to remember to go looking for
 * them. Being the first screen also makes it, most mornings, the read that
 * WRITES the reader's insights for the day; the sheet then reads the same ones
 * for free.
 *
 * THE CARD GREETS THE READER BY NAME. Its title is the greeting itself, because
 * this card is the product speaking to one person about their site — which is
 * what separates it from every labelled card around it.
 *
 * IT IS THE WRITTEN PART, NOT THE LEDGER. The assistant's sheet carries both;
 * the dashboard already STATES the numbers in cards of their own, so a second
 * copy of them would be the same site said twice. The full sheet stays one press
 * away (below). The card never reads the brief itself: the insights carry their
 * day and each section's state.
 *
 * A QUESTION LEAVES THE DASHBOARD. There is no thread on this surface, so an
 * insight's question opens the assistant with it already asked (`?ask=`) —
 * the answer lands in a real thread, exactly as if it had been typed there.
 */
export function MorningBriefCard() {
  const { t, i18n } = useTranslation();
  const { siteId = '' } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const greeting = useNamedGreeting();
  // The same query the panel below reads, so the day costs no second request.
  const { data } = useBriefInsights(siteId);

  const openBrief = useCallback(() => {
    navigate(`/ai-agent/${encodeURIComponent(siteId)}?view=brief`);
  }, [navigate, siteId]);

  const ask = useCallback(
    // The question AND where it came from: the assistant reads that section's
    // rows on the way in, so the answer starts from what the card showed.
    (question: string, briefRef?: AiBriefRef) => {
      const params = new URLSearchParams({ ask: question });
      if (briefRef) params.set('from', briefRef.section);
      if (briefRef?.insight !== undefined) params.set('insight', String(briefRef.insight));
      navigate(`/ai-agent/${encodeURIComponent(siteId)}?${params.toString()}`);
    },
    [navigate, siteId],
  );

  return (
    <DashboardCard
      title={greeting}
      titleAppearance="prose"
      headerRight={
        <button type="button" className={styles.open} onClick={openBrief}>
          {t('dashboard.morningBrief.openBrief')}
          <ArrowRightIcon size={14} />
        </button>
      }
      bodyClassName={styles.body}
    >
      {/* The day the reading is about. It waits on the read; the greeting,
          being the time of day, does not. */}
      {data && (
        <p className={styles.dateline}>
          <time className={styles.day} dateTime={data.date}>
            {formatBriefDate(data.date, i18n.language)}
          </time>
          {/* The card has no ledger, so a mute takes nothing visible off it — but
              a reader who muted safety must not read this panel as the whole
              morning. The count says the brief has been narrowed; the sheet names
              which sections and offers them back. */}
          {data.mutedSections.length > 0 && (
            <span className={styles.muted}>
              {t('aiAgent.briefPrefs.mutedFooter', { count: data.mutedSections.length })}
            </span>
          )}
        </p>
      )}
      <BriefInsights siteId={siteId} onAsk={ask} />
    </DashboardCard>
  );
}
